/* eslint-disable prettier/prettier */

import { useCallback, useEffect, useState } from 'react';
import {
	goalTypeService,
	GoalType,
	CreateGoalTypePayload,
	UpdateGoalTypePayload,
} from '../services/goalType.service';

/**
 * Custom Hook:
 * Handles all frontend logic related to Goal Types
 */
export const useGoalType = () => {
	/**
	 * Goal type list state
	 */
	const [goalTypes, setGoalTypes] = useState<GoalType[]>([]);

	/**
	 * Loading states
	 */
	const [loading, setLoading] = useState<boolean>(false);
	const [creating, setCreating] = useState<boolean>(false);
	const [updating, setUpdating] = useState<boolean>(false);
	const [deleting, setDeleting] = useState<boolean>(false);

	/**
	 * Error state
	 */
	const [error, setError] = useState<string | null>(null);

	/**
	 * Fetch Goal Types
	 */
	const fetchGoalTypes = useCallback(
		async (includeInactive: boolean = false) => {
			try {
				setLoading(true);
				setError(null);

				const response =
					await goalTypeService.getGoalTypes(includeInactive);

				if (response.success && response.data) {
					setGoalTypes(response.data);
				} else {
					setError(response.message || 'Failed to fetch goal types');
				}
			} catch (err: any) {
				console.error('Fetch Goal Types Error:', err);

				setError(
					err.message ||
					'Something went wrong while fetching goal types'
				);
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	/**
	 * Create Goal Type
	 */
	const createGoalType = async (
		payload: CreateGoalTypePayload
	): Promise<GoalType | null> => {
		try {
			setCreating(true);
			setError(null);

			const response = await goalTypeService.createGoalType(payload);

			if (response.success && response.data) {
				/**
				 * Instant UI update
				 * Adds newly created goal type immediately
				 */
				setGoalTypes(prev => [...prev, response.data!]);

				return response.data;
			}

			setError(response.message || 'Failed to create goal type');
			return null;
		} catch (err: any) {
			console.error('Create Goal Type Error:', err);

			setError(
				err.message || 'Something went wrong while creating goal type'
			);

			return null;
		} finally {
			setCreating(false);
		}
	};

	/**
	 * Update Goal Type
	 */
	// const updateGoalType = async (
	//     goalTypeId: string,
	//     payload: UpdateGoalTypePayload
	// ): Promise<boolean> => {
	//     try {
	//         setUpdating(true);
	//         setError(null);

	//         const response =
	//             await goalTypeService.updateGoalType(
	//                 goalTypeId,
	//                 payload
	//             );

	//         if (response.success && response.data) {

	//             /**
	//              * Instant UI refresh after update
	//              */
	//             setGoalTypes(prev =>
	//                 prev.map(goalType =>
	//                     goalType.id === goalTypeId
	//                         ? response.data!
	//                         : goalType
	//                 )
	//             );

	//             return true;
	//         }

	//         setError(response.message || 'Failed to update goal type');
	//         return false;

	//     } catch (err: any) {
	//         console.error('Update Goal Type Error:', err);

	//         setError(
	//             err.message || 'Something went wrong while updating goal type'
	//         );

	//         return false;
	//     } finally {
	//         setUpdating(false);
	//     }
	// };

	/**
	 * Delete / Deactivate Goal Type
	 */
	const deleteGoalType = async (goalTypeId: string): Promise<boolean> => {
		try {
			setDeleting(true);
			setError(null);

			const response = await goalTypeService.deleteGoalType(goalTypeId);

			if (response.success) {
				/**
				 * Instant UI update after deletion
				 */
				setGoalTypes(prev =>
					prev.filter(goalType => goalType.id !== goalTypeId)
				);

				return true;
			}

			setError(response.message || 'Failed to delete goal type');
			return false;
		} catch (err: any) {
			console.error('Delete Goal Type Error:', err);

			setError(
				err.message || 'Something went wrong while deleting goal type'
			);

			return false;
		} finally {
			setDeleting(false);
		}
	};

	/**
	 * Auto fetch on initial render
	 */
	useEffect(() => {
		fetchGoalTypes();
	}, [fetchGoalTypes]);

	return {
		/**
		 * States
		 */
		goalTypes,
		loading,
		creating,
		updating,
		deleting,
		error,

		/**
		 * Methods
		 */
		fetchGoalTypes,
		createGoalType,
		// updateGoalType,
		deleteGoalType,
	};
};
